import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, RotateCcw, ChevronRight, Loader2, Filter, FileText, RefreshCcw } from 'lucide-react';
import { swSearchProjects, swGetSuggestedFilters } from '../../services/reportingHubApi';

const PAGE_SIZE = 10;

const SoftwareSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restored = useRef(!!location.state);
  const restoredState = useRef(!!location.state);
  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  const [keywords, setKeywords] = useState(
    location.state?.keywords || ''
  );

  const [selectedFilters, setSelectedFilters] = useState(
    location.state?.selectedFilters || []
  );

  const [results, setResults] = useState(
    location.state?.results || []
  );

  const [total, setTotal] = useState(
    location.state?.total || 0
  );

  const [offset, setOffset] = useState(
    location.state?.offset || 0
  );



  const toggleFilter = (token) =>
    setSelectedFilters((prev) => (prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token]));
  const reset = () => { setKeywords(''); setSelectedFilters([]); };

  const loadChips = useCallback(async (kw, filters) => {
    try {
      const data = await swGetSuggestedFilters({ keywords: kw, filters });
      setChips(data.chips || []);
    } catch { /* non-critical */ }
  }, []);

  const runSearch = useCallback(async (kw, filters, nextOffset = 0, append = false) => {
    setLoading(true);
    setError('');
    try {
      const data = await swSearchProjects({ keywords: kw, filters, offset: nextOffset, limit: PAGE_SIZE });
      setTotal(data.total || 0);
      setOffset(nextOffset);
      setResults((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
    } catch (e) {
      setError(e.response?.status === 503 ? 'Source database unavailable.' : 'Search failed, please retry.');
      if (!append) setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (restoredState.current) {
      restoredState.current = false;
      loadChips(keywords, selectedFilters);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      runSearch(keywords, selectedFilters, 0, false);
      loadChips(keywords, selectedFilters);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [keywords, selectedFilters, runSearch, loadChips]);

  const showMore = () => runSearch(keywords, selectedFilters, offset + PAGE_SIZE, true);
  const hasMore = results.length < total;

  return (
    <div className="min-h-screen bg-black text-white p-2 sm:p-2 md:p-2 lg:px-5 lg:py-4 mb-1">
      <div className="w-full mx-auto">

        <div className="flex items-center gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-400 sm:mb-5 sm:-ml-4">
          <button
            onClick={() => navigate('/project-reporting')}
            className="p-1 sm:p-2 rounded-full hover:bg-gray-800 transition-colors flex-shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-7 md:h-7 font-bold" />
          </button>

          <h1
            className="
      text-sm
      sm:text-lg
      md:text-2xl
      lg:text-3xl
      xl:text-4xl
      font-bold
      leading-tight
      break-words
    "
          >
            Project Reporting Hub_Software
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
          <label className="font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-lg">
            Keyword search
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:mb-5">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-black" />
            <input
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="Search by project ID, project name, region, platform, vendor, model name"
              className="
        w-full
        bg-white
        text-black
        rounded-lg
        pl-10 sm:pl-11
        pr-4
        py-2.5 sm:py-3
        text-sm sm:text-base
        outline-none
        focus:ring-2
        focus:ring-blue-500
      "
            />
          </div>

          {/* Mobile Reset Button */}
          <div className="flex justify-end sm:hidden">
            <button
              onClick={reset}
              className="
        flex items-center gap-1
        px-3 py-2
        rounded-lg
        bg-gray-700
        hover:bg-gray-600
        transition-colors
        text-sm
      "
            >

              Reset
              <RefreshCcw className="w-4 h-4" />
            </button>
          </div>

          {/* Desktop Reset Button */}
          <button
            onClick={reset}
            className="
      hidden sm:flex
      items-center gap-2
      px-4 py-3
      rounded-lg
      bg-gray-700
      hover:bg-gray-600
      transition-colors
      whitespace-nowrap
    "
          >

            Reset
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
          <label className="font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-lg">
            Suggested filters
          </label>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-2 mb-3 pb-3 sm:pb-4 md:pb-5 border-b-2 border-gray-400">
          {chips.length === 0 && (
            <span className="text-gray-500 text-sm col-span-full">
              No suggestions
            </span>
          )}

          {chips.map((chip) => {
            const active = selectedFilters.includes(chip.token);

            return (
              <button
                key={`${chip.category}-${chip.token}`}
                onClick={() => toggleFilter(chip.token)}
                title={`${chip.category} · ${chip.count}`}
                className={`w-full px-1.5 sm:px-3 py-1 sm:py-2 rounded-full text-[10px] sm:text-xs md:text-sm font-medium transition-colors truncate ${active
                    ? "bg-gray-500 text-white"
                    : "bg-white text-black hover:bg-gray-200"
                  }`}
              >
                {chip.token}
              </button>
            );
          })}
        </div>

        <hr className="border-gray-800 mb-4" />
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />

            <h2 className="block font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-lg">
              Results{" "}
              {total > 0 && (
                <span className="text-gray-400 text-sm">
                  ({total})
                </span>
              )}
            </h2>
          </div>

          <span className="block font-semibold p-2 text-xs sm:text-sm md:text-base lg:text-base text-gray-200">
            Tips: Add keywords or select more filters to narrow down the list
          </span>
        </div>

        {error && <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-lg px-4 py-3 mb-4">{error}</div>}

        <div className="space-y-2">
          {results.map((p) => (
            <button
              key={p.projectId}
              // onClick={() =>
              //   navigate(`/reporting-hub/software/${p.projectId}`)
              // }
              onClick={() =>
                // navigate(`/reporting-hub/hardware/${p.projectId}`)
                navigate(`/reporting-hub/software/${p.projectId}`, {
                  state: {
                    keywords,
                    selectedFilters,
                    results,
                    total,
                    offset
                  }
                })
              }
              className="
        w-full
        flex flex-col sm:flex-row
        sm:items-center sm:justify-between
        gap-2
        px-3 sm:px-4 md:px-5
        py-3 sm:py-4
        bg-black
        hover:bg-gray-800
        border-b-2 border-dotted border-gray-400
        text-left
        transition-colors
        text-white
      "
            >
              <span
                className="
          font-mono
          text-xs sm:text-sm md:text-base
          break-words
          w-full
          text-white
        "
              >
                #{p.projectId}, {p.name}
              </span>

              <ChevronRight
                className="
          w-5 h-5 sm:w-7 sm:h-7
          text-gray-400
          self-end sm:self-center
          flex-shrink-0
        "
              />
            </button>
          ))}
        </div>

        {loading && <div className="flex items-center justify-center py-6 text-gray-400">
          <Loader2 className="sm:w-15 sm:h-15 w-6 h-6 animate-spin text-blue-800" />
        </div>}
        {!loading && results.length === 0 && !error && <div className="text-center text-gray-500 py-10">No projects found.</div>}
        {hasMore && !loading && (
          <div className="flex justify-center mt-5">
            <button onClick={showMore} className="px-6 py-2 rounded-lg bg-gray-700 hover:bg-gray-600">Show more</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoftwareSearch;
